import React, { useCallback, useEffect, useRef, useState } from 'react';
import AppRoutes from '../routes/app';
import { FavIcon, ToastContainer } from '@totalsoft/rocket-ui';
import Header from './layout/Header';
import Sidebar from './layout/Sidebar';
import Footer from './layout/Footer';
import Main from './layout/Main';
import { getApplicationLogo } from '../utils/theme';
import AreasProvider from '../providers/AreasProvider';
import { RightsContext } from '../providers/RightsContext';
import GlobalLoadingIndicator from 'features/common/components/GlobalLoadingIndicator';
import GlobalSubscriptionArea from 'features/common/components/GlobalSubscriptionArea';
import { useTranslation } from 'react-i18next';
import { USER_INFO_QUERY, RIGHTS_INFO_QUERY } from './queries';
import { useQuery } from 'hooks/apollo';
import EntityTransferActions from 'features/common/entityTransfer/EntityTransferActions';
import { getOidcConfigName } from 'utils/functions';
import { useMediaQuery, useTheme } from '@mui/material';
import { Container, Content } from './AppStyle';
import { useOidcUser, useOidcAccessToken } from '@axa-fr/react-oidc';
import { env } from 'utils/env';
import { useIdleTimer } from 'react-idle-timer';
import { useOidc } from '@axa-fr/react-oidc';
import WrappedFakeText from './common/WrappedFakeText';
import GlobalConfirmDialog from 'features/common/components/confirm/GlobalConfirmDialog';
import configureMonacoFormulaEditor from 'utils/configureMonacoFormulaEditor';
import { useI18nextGlobalTextResolver } from '@ts-private/list-browser-i18next-extensions';
import { closeSocket } from 'apollo/client';
import { useApolloClient } from '@apollo/client';
import { RightSideButtonsWrapper } from 'components/layout/RightSideBarStyle';
import RightSideBarButtons from 'components/layout/RightSideBarButtons';
import rightSidebarDrawers from 'constants/rightSidebarDrawers';
import RightSideBar from './layout/RightSideBar';
import GlobalPrompt from 'features/common/dirtyInfo/components/GlobalPrompt';
import defaultFavIcon from 'assets/img/defaultFavIcon.ico';
import GlobalContextSetter from './menu/GlobalContextSetter';

const App = () => {
  const { i18n } = useTranslation();
  const { oidcUser } = useOidcUser(getOidcConfigName());
  const { accessToken } = useOidcAccessToken(getOidcConfigName());
  const theme = useTheme();
  const overMd = useMediaQuery(theme.breakpoints.up('md'));
  const mainPanelRef = useRef();
  const { logout } = useOidc(getOidcConfigName());
  var client = useApolloClient();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMinimized, setDrawerMinimized] = useState(true);

  const changeLanguage = useCallback(
    lng => {
      i18n.changeLanguage(lng.target.value).then(() => {
        client.resetStore();
        closeSocket('Language changed');
      });
    },
    [i18n, client]
  );
  const handleDrawerMinimizeToggle = useCallback(() => {
    setDrawerMinimized(prev => !prev);
  }, []);

  const handleDrawerOpenToggle = useCallback(() => {
    setDrawerOpen(prev => !prev);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  useIdleTimer({
    timeout: 1000 * env.REACT_APP_IDLE_TIMEOUT,
    onIdle: () => {
      console.log('user is idle');
    },
    onActive: _ => {
      logout();
    },
    debounce: 500,
    crossTab: true
  });

  useEffect(() => {
    mainPanelRef.current.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (overMd) setDrawerOpen(false);
  }, [overMd]);

  configureMonacoFormulaEditor();

  const { data: userInfo } = useQuery(USER_INFO_QUERY, {
    skip: !oidcUser
  });

  const {
    data: rightsInfo,
    loading: loadingRightsInfo,
    refetch
  } = useQuery(RIGHTS_INFO_QUERY, {
    skip: !oidcUser
  });

  const [stateDrawer, setStateDrawer] = useState({
    summaryDrawerOpen: false,
    rightSidebarButtonsVisible: false
  });

  const handleRightDrawerToggle = (drawerName, closeAll = false) => {
    switch (drawerName) {
      case rightSidebarDrawers.summary:
        setStateDrawer({
          ...stateDrawer,
          summaryDrawerOpen: !stateDrawer.summaryDrawerOpen && !closeAll
        });
        break;
      default:
        break;
    }
  };

  const showRightSideBar = visible => {
    setStateDrawer({
      summaryDrawerOpen: stateDrawer.summaryDrawerOpen && visible,
      rightSidebarButtonsVisible: visible
    });
  };

  useI18nextGlobalTextResolver();

  const hasAccess = rightsInfo?.rightsInfo?.hasAccess;
  return (
    <React.Fragment>
      <AreasProvider>
        <GlobalContextSetter />
        <Container>
          <Sidebar
            closeDrawer={handleCloseDrawer}
            changeLanguage={changeLanguage}
            drawerOpen={drawerOpen}
            drawerMinimized={drawerMinimized}
            hasAccess={hasAccess}
            userInfo={userInfo?.userInfo}
          />
          <RightSideBar
            showRightSideBar={showRightSideBar}
            handleRightDrawerToggle={handleRightDrawerToggle}
            open={stateDrawer.summaryDrawerOpen}
            drawerName={stateDrawer.summaryDrawerOpen ? rightSidebarDrawers.summary : ''}
          />
          <Content ref={mainPanelRef} drawerMinimized={drawerMinimized} rightSideOpened={stateDrawer.summaryDrawerOpen}>
            {userInfo?.userInfo?.userId && accessToken && (
              <FavIcon favIconSource={getApplicationLogo(true, accessToken)} defaultFavIcon={defaultFavIcon} />
            )}
            <Header
              drawerMinimized={drawerMinimized}
              handleDrawerMinimizeToggle={handleDrawerMinimizeToggle}
              handleDrawerOpenToggle={handleDrawerOpenToggle}
            />
            {stateDrawer.rightSidebarButtonsVisible && (
              <RightSideButtonsWrapper>
                <RightSideBarButtons handleRightDrawerToggle={handleRightDrawerToggle} />
              </RightSideButtonsWrapper>
            )}
            <RightsContext.Provider
              value={{
                rights: rightsInfo?.rightsInfo || {
                  isReadOnly: true,
                  isAdmin: false
                },
                refetchRightsInfo: refetch,
                loaded: Boolean(!loadingRightsInfo && rightsInfo?.rightsInfo)
              }}
            >
              {!loadingRightsInfo ? (
                <Main>
                  <AppRoutes />
                </Main>
              ) : (
                <WrappedFakeText lines={10} p={3} />
              )}
              <EntityTransferActions />
            </RightsContext.Provider>
            {oidcUser && <GlobalSubscriptionArea />}
            <Footer fluid />
          </Content>
        </Container>
      </AreasProvider>
      <GlobalLoadingIndicator />
      <GlobalConfirmDialog />
      <GlobalPrompt />
      <ToastContainer theme='colored' />
    </React.Fragment>
  );
};
export default App;
